// src/utils/apiConfig.js
// Configuración centralizada para URLs de API

/**
 * Obtiene la URL base de la API
 * En desarrollo: usa VITE_API_URL del .env o localhost:5000
 * En producción: usa el mismo dominio que el frontend
 */
export const getApiBaseUrl = () => {
  // 1) Vite env (guardado para evitar errores cuando import.meta no existe)
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
      return import.meta.env.VITE_API_URL;
    }
  } catch (_) {
    // ignorar
  }

  // 2) CRA/webpack env fallback
  try {
    const envFromProcess = (typeof process !== 'undefined' && process.env)
      ? (process.env.VITE_API_URL || process.env.REACT_APP_API_URL)
      : undefined;
    if (envFromProcess) return envFromProcess;
  } catch (_) {
    // ignorar
  }

  // 3) Meta tag fallback (opcional)
  try {
    const meta = document.querySelector('meta[name="api-base-url"]');
    const content = meta && meta.getAttribute('content');
    if (content) return content;
  } catch (_) {
    // ignorar
  }

  // 4) Fallback: detectar si es local o producción
  const host = (typeof window !== 'undefined' && window.location) ? window.location.hostname : 'localhost';
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  return isLocal ? 'http://localhost:5000' : origin;
};

/**
 * Construye una URL completa para un endpoint de API
 * @param {string} endpoint - El endpoint (ej: '/api/visitas')
 * @returns {string} - URL completa
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  const base = String(baseUrl || '').replace(/\/+$/, ''); // sin trailing slash
  const ep = String(endpoint || '');
  const path = ep.startsWith('/') ? ep : `/${ep}`; // asegurar leading slash
  return `${base}${path}`;
};

/**
 * Configuración por defecto para fetch con credenciales
 */
export const fetchConfig = {
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  }
};

/**
 * Wrapper para fetch con configuración automática
 * @param {string} endpoint - El endpoint de la API
 * @param {object} options - Opciones adicionales para fetch
 * @returns {Promise} - Promesa de fetch
 */
export const apiFetch = (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  const config = {
    ...fetchConfig,
    ...options,
    headers: {
      ...fetchConfig.headers,
      ...options.headers,
    }
  };
  
  return fetch(url, config);
};

// Exportar también la función para facilitar imports
export default {
  getApiBaseUrl,
  getApiUrl,
  fetchConfig,
  apiFetch
};
